import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import GearIcon from '#/assets/settarit.svg?react';
import SignoutIcon from '#/assets/logout.svg?react';
import editIcon from '#/assets/edit.svg';
import editAllIcon from '#/assets/editAll.svg';
import selectAllIcon from '#/assets/select-all.svg';
import clearIcon from '#/assets/clear.svg';

import { updateSettings } from '#/api/settings';
import { logout } from '#/api/auth';
import { useWebSocket, WebSocketCommand } from '#/contexts/WebsocketContext';

import './settings.css';
import { useRecording } from '#/contexts/RecordingContext';
import { useMsal } from '@azure/msal-react';
import { Language, RecordingMode } from '#/types/preferences';
import logger from "#/utils/logger";
import { FeatureSelection } from '../feature-selection/feature-selection';
import { clearFeature, getFeature, sendFeatureSelections } from '#/api/feature';
import { Feature, FeatureRecord } from '#/types/message';
import Notification from '#/components/notification/notification';

const LANGUAGE_LABELS: Partial<Record<Language, string>> = {
  [Language.EN]: 'English',
  [Language.DE]: 'Deutsch',
  [Language.FR]: 'Français',
  [Language.FI]: 'Suomi',
  [Language.ES]: 'Español',
  [Language.PT]: 'Português',
};

interface SettingsProps {
  machineLanguages: Language[];
  selectedLanguage: Language;
  setSelectedLanguage: (language: Language) => void;
  machines: string[];
  isMuted: boolean;
  setIsMuted: (muted: boolean) => void;
  exhibitionModeEnabled: boolean;
  setExhibitionModeEnabled: (exhibition_mode: boolean) => void;
  selectedMachine: string;
  selectedFeatures: Record<string, [string, string | boolean | null]>;
  setSelectedFeatures: (features: Record<string, [string, string | boolean | null]>) => void;
  serialNumber: string;
  setSerialNumber: (value: string) => void;
  backendVersion?: string;
}

const Settings: React.FC<SettingsProps> = ({
  machineLanguages,
  selectedLanguage,
  setSelectedLanguage,
  machines,
  isMuted, setIsMuted,
  exhibitionModeEnabled, setExhibitionModeEnabled,
  selectedMachine,
  selectedFeatures,
  setSelectedFeatures,
  serialNumber,
  setSerialNumber,
  backendVersion,
}) => {
  const { t, i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const { isRecording, cancelRecording, resumeRecording, startRecording, recordingMode, setRecordingMode,
          isMicAlwaysOn, setIsMicAlwaysOn, pauseRecording } = useRecording();
  const modalRef = useRef<HTMLDivElement>(null);
  const { sendMessage } = useWebSocket();
  const [userName, setUserName] = useState("");
  const { instance } = useMsal();
  const [activeTab, setActiveTab] = useState('general');
  const [pendingMachine, setPendingMachine] = useState<string | null>(null);
  const [editingFeature, setEditingFeature] = useState<Feature | Feature[] | null>(null);
  const [batchEditMode, setBatchEditMode] = useState(false);
  const [selectedFeaturesToEdit, setSelectedFeaturesToEdit] = useState<string[]>([]);
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [versionClicks, setVersionClicks] = useState(0);
  const [showReset, setShowReset] = useState(false);

  const handleOpenSettings = () => {
    setIsOpen(true);
    if (isRecording) {
      cancelRecording();
    }
    pauseRecording();
  };

  const handleVersionClick = () => {
    setVersionClicks(prev => prev + 1);
    if (versionClicks + 1 >= 3) {
      // Show the reset button
      setShowReset(true);
    }
  };

  const handleSignout = async () => {
    await logout()
  }

  const handleResetOthersToStartScreen = () => {
    sendMessage(
      JSON.stringify({
        command: WebSocketCommand.SHIFT_OTHERS_TO_START_SCREEN,
        payload: { origin: 'self' },
      })
    );
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('openSettings') === 'true') {
      setIsOpen(true);
      const tab = urlParams.get('tab');
      if (tab === 'machine' || tab === 'general') {
        setActiveTab(tab);
      }
      const newUrl = window.location.pathname + window.location.hash;
      window.history.replaceState({}, '', newUrl);
    }
  }, []);

  const handleSettingChange = async (
    type: 'language' | 'machine' | 'muted' | 'recordingMode' | 'isMicAlwaysOn' | 'exhibitionModeEnabled' | 'serialNumber',
    value: any
  ) => {
    const serialAsNumber =
      type === "serialNumber"
        ? (String(value ?? "").trim() === "" ? null : Number(value))
        : undefined;

    try {
      const updatedSettings = {
        language: type === 'language' ? value : selectedLanguage,
        muted: type === 'muted' ? value : isMuted,
        machine: type === 'machine' ? value : selectedMachine,
        recordingMode: type === 'recordingMode' ? value : recordingMode,
        isMicAlwaysOn: type === 'isMicAlwaysOn' ? value : isMicAlwaysOn,
        exhibitionModeEnabled: type === 'exhibitionModeEnabled' ? value : exhibitionModeEnabled,
        serialNumber: serialAsNumber,
      };

      await updateSettings(
        updatedSettings.language,
        updatedSettings.muted,
        updatedSettings.machine,
        updatedSettings.recordingMode,
        updatedSettings.isMicAlwaysOn,
        updatedSettings.exhibitionModeEnabled,
        updatedSettings.serialNumber
      );

      switch (type) {
        case 'machine':
        case 'language':
          if (exhibitionModeEnabled) {
            setIsOpen(false);
            window.location.reload();
            return;
          }
          if (value !== (type === 'machine' ? selectedMachine : selectedLanguage)) {
            if (type === 'machine') {
              setPendingMachine(value);
            }
            const loadingMessage = document.createElement('div');
            loadingMessage.className = 'loading-overlay-settings';
            loadingMessage.innerHTML = `
              <div class="loading-content-settings">
                <div class="loading-spinner-settings"></div>
                <p>${t(type === 'machine' ? "loadingNewMachine" : "changingLanguage")}</p>
              </div>
            `;
            document.body.appendChild(loadingMessage);

            const newUrl = window.location.pathname + `?openSettings=true&tab=${type}` + window.location.hash;
            window.history.replaceState({}, '', newUrl);
            setTimeout(() => {
              window.location.reload();
            }, 500);
            return;
          }
          if (type === 'machine') {
            setPendingMachine(value);
          } else {
            setSelectedLanguage(value);
            i18n.changeLanguage(value);
          }
          break;
        case 'muted':
          setIsMuted(value);
          break;
        case 'recordingMode':
          setRecordingMode(value);
          break;
        case 'isMicAlwaysOn':
          setIsMicAlwaysOn(value);
          break;
        case 'exhibitionModeEnabled':
          setExhibitionModeEnabled(value);
          break;
        case 'serialNumber':
          setSerialNumber(value);
          break;
      }
    } catch (error) {
      logger.error(`Error updating ${type}:`, error);
    }
  };

  const handleIsMicAlwaysOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleSettingChange('isMicAlwaysOn', e.target.checked);
  };

  const handleIsExhibitionModeEnabledChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleSettingChange('exhibitionModeEnabled', e.target.checked);
  };

  const closeModal = () => {
    if (editingFeature) {
      // Don't close if feature selection is open
      return;
    }

    if (pendingMachine && pendingMachine !== selectedMachine) {
      const loadingMessage = document.createElement('div');
      loadingMessage.className = 'loading-overlay-settings';
      loadingMessage.innerHTML = `
        <div class="loading-content-settings">
          <div class="loading-spinner-settings"></div>
          <p>${t("loadingNewMachine")}</p>
        </div>
      `;
      document.body.appendChild(loadingMessage);

      setTimeout(() => {
        window.location.reload();
      }, 500);
    } else {
      if (recordingMode === RecordingMode.Automatic) {
        startRecording();
      }
      resumeRecording();
    }

    setIsOpen(false);
    setPendingMachine(null);
  };

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (modalRef.current &&
        !modalRef.current.contains(event.target as Node) &&
        !document.querySelector('.feature-selection-panel')?.contains(event.target as Node)) {
        closeModal();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleOutsideClick);
    }

    const isAuthDisabled = import.meta.env.VITE_DISABLE_AUTH === 'true';

    if (isAuthDisabled) {
      setUserName("testuser");
    } else {
      const userName = instance.getAllAccounts()[0].username;
      setUserName(userName);
    }

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [isOpen, instance, editingFeature]);

  const formatFeatureValue = (value: string | boolean | null) => {
    if (value === null) return '—';
    if (typeof value === 'boolean') return value ? t("yes") : t("no");
    return value.toString();
  };

  const handleFeatureEdit = async (featureKey: string) => {
    try {
      const featureDetails = await getFeature(featureKey);
      setEditingFeature(featureDetails);
    } catch (error) {
      logger.error('Failed to fetch feature details:', error);
    }
  };

  const handleFeatureClear = async (featureKey: string) => {
    try {
      await clearFeature(featureKey);

      // Create a new features object with the cleared feature
      const newFeatures = {...selectedFeatures};

      // Set the selected option to null for the cleared feature
      if (newFeatures[featureKey]) {
        newFeatures[featureKey] = [newFeatures[featureKey][0], null];
      }

      setSelectedFeatures(sortFeatures(newFeatures));
      setNotificationMessage(t('featureCleared'));
      setShowNotification(true);
    } catch (error) {
      setNotificationMessage(t('featureClearFailed'));
      setShowNotification(true);
      logger.error('Failed to clear feature:', error);
    }
  };

  const sortFeatures = (features: FeatureRecord): FeatureRecord => {
    return Object.entries(features)
      .sort(([, [nameA]], [, [nameB]]) => nameA.localeCompare(nameB))
      .reduce<FeatureRecord>((obj, [key, value]) => {
        obj[key] = value;
        return obj;
      }, {});
  };

  const handleFeatureSelectionComplete = async (selections: { feature_name: string, option_name: string }[]) => {
    try {
      const updatedFeatures = await sendFeatureSelections(selections);
      setSelectedFeatures(sortFeatures(updatedFeatures));
      setNotificationMessage(t('featureUpdated'));
      setShowNotification(true);
    } catch (error) {
      logger.error('Failed to update feature:', error);
      setNotificationMessage(t('featureUpdateFailed'));
      setShowNotification(true);
    }
    setEditingFeature(null);
  };

  return (
    <>
      <div className="settings-container" onClick={handleOpenSettings}>
        <GearIcon id="gear" aria-label="Settings" />
        <p className="language-label">{selectedLanguage.toUpperCase()}</p>
      </div>
      {isOpen && (
        <div className="modal-overlay">
          <div
            className="settings-modal"
            ref={modalRef}
            data-native-scroll>
          <div className="settings-modal-top-bar">
            <span>{t("settings")}</span>
            <button className="close-button" onClick={closeModal}>×</button>
          </div>
            <div className="modal-header-container">
              <div className="settings-tabs">
                <button
                  className={`tab-button ${activeTab === 'general' ? 'active' : ''}`}
                  onClick={() => setActiveTab('general')}
                >
                  {t("general")}
                </button>
                <button
                  className={`tab-button ${activeTab === 'machine' ? 'active' : ''}`}
                  onClick={() => setActiveTab('machine')}
                >
                  {t("machine")}
                </button>
                <button
                  className={`tab-button ${activeTab === 'info' ? 'active' : ''}`}
                  onClick={() => setActiveTab('info')}
                >
                  {t("info")}
                </button>
              </div>
            </div>

            <div className="settings-form">
              {activeTab === 'general' && (
                <div className="tab-content general-tab">

                  <div className="form-group">
                    <label className="form-label">{t("language")}</label>
                    <div className="radio-toggle-group">
                      {machineLanguages.map(lang => (
                        <label key={lang} className="radio-item">
                          <input
                            type="radio"
                            name="language"
                            value={lang}
                            checked={selectedLanguage === lang}
                            onChange={() => handleSettingChange('language', lang)}
                          />
                          <span className="custom-circle" />
                          <span className="radio-label">{LANGUAGE_LABELS[lang]}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">{t("audioPlayback")}</label>
                    <div className="radio-toggle-group">
                      {[
                        { label: t('on'), value: false },
                        { label: t('off'), value: true }
                      ].map(option => (
                        <label key={option.label} className="radio-item">
                          <input
                            type="radio"
                            name="audioPlayback"
                            value={String(option.value)}
                            checked={isMuted === option.value}
                            onChange={() => handleSettingChange('muted', option.value)}
                          />
                          <span className="custom-circle" />
                          <span className="radio-label">{t(option.label)}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">{t("recordingMode")}</label>
                    <div className="radio-toggle-group">
                      {[
                        { label: t("automatic"), value: RecordingMode.Automatic },
                        { label: t("clickToTalk"), value: RecordingMode.ClickToTalk },
                        { label: t("pushToTalk"), value: RecordingMode.PushToTalk }
                      ].map(option => (
                        <label key={option.value} className="radio-item">
                          <input
                            type="radio"
                            name="recordingMode"
                            value={option.value}
                            checked={recordingMode === option.value}
                            onChange={() => handleSettingChange("recordingMode", option.value)}
                          />
                          <span className="custom-circle" />
                          <span className="radio-label">{option.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="form-group-horizontal checkbox-wrapper">
                    <label className="custom-checkbox">
                      <input
                        type="checkbox"
                        id="recordingMode"
                        checked={recordingMode === RecordingMode.Automatic || isMicAlwaysOn}
                        disabled={recordingMode === RecordingMode.Automatic}
                        onChange={handleIsMicAlwaysOnChange}
                      />
                      <span className={`checkmark ${recordingMode === RecordingMode.Automatic ? 'disabled' : ''}`} />
                      <span className="radio-label">{t("keepMicrophoneAlwaysOn")}</span>
                    </label>
                  </div>

                    <div className="signout-section">
                      <div className="signout-menu" onClick={handleSignout}>
                        <SignoutIcon id="signout" />
                        <span className="signout-label">{t("signout")}</span>
                      </div>

                      <div className="bottom-row">
                        <div className="username">{userName}</div>
                      </div>
                    </div>
                </div>
              )}

              {activeTab === 'machine' && (
                <div className="tab-content">
                  <div className="form-group">
                    <label htmlFor="machineSelect">{t("machine")}</label>
                    <select
                      id="machineSelect"
                      value={pendingMachine || selectedMachine}
                      onChange={(e) => handleSettingChange('machine', e.target.value)}
                    >
                      {machines.map((machine) => (
                        <option key={machine} value={machine}>
                          {machine}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">{t("serialNumber")}</label>

                    <input
                      type="text"
                      className="serial-input"
                      value={serialNumber}
                      inputMode="numeric"
                      onChange={(e) => {
                        const digitsOnly = e.target.value.replace(/[^\d]/g, "");
                        setSerialNumber(digitsOnly);
                      }}
                      onBlur={(e) => {
                        const digitsOnly = e.target.value.replace(/[^\d]/g, "");
                        handleSettingChange("serialNumber", digitsOnly);
                      }}
                      disabled={exhibitionModeEnabled}
                    />

                    <div className="serial-hint">
                      {t("serialNumberHint") || "Digits only. Leave empty if unknown."}
                    </div>
                  </div>
                  {Object.keys(selectedFeatures).length > 0 && (
                    <div className="machine-features">
                      <div className="features-header">
                        <label>{t("features")}</label>
                        <button
                          className="feature-edit-button"
                          onClick={() => setBatchEditMode(!batchEditMode)}
                          disabled={exhibitionModeEnabled}
                        >
                          <img src={editAllIcon} alt="edit all" className="edit-icon" />
                        </button>

                        <div className="features-header-controls">
                          {batchEditMode && (
                            <button
                              className="select-all-button"
                              onClick={() => {
                                if (selectedFeaturesToEdit.length === Object.keys(selectedFeatures).length) {
                                  setSelectedFeaturesToEdit([]);
                                } else {
                                  setSelectedFeaturesToEdit(Object.keys(selectedFeatures));
                                }
                              }}
                              disabled={exhibitionModeEnabled}
                            >
                              <img
                                src={selectAllIcon}
                                alt="select all"
                                className={`select-all-icon ${selectedFeaturesToEdit.length === Object.keys(selectedFeatures).length ? 'selected' : ''}`}
                              />
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="features-list">
                        {Object.entries(selectedFeatures).map(([key, [name, value]]) => (
                          <div
                            key={key}
                            className={`feature-item ${batchEditMode ? 'selectable' : ''}`}
                            onClick={() => {
                              if (batchEditMode && !exhibitionModeEnabled) {
                                setSelectedFeaturesToEdit(prev =>
                                  prev.includes(key)
                                    ? prev.filter(k => k !== key)
                                    : [...prev, key]
                                );
                              }
                            }}
                          >
                            <div className="feature-info">
                              <span className="feature-key">{name}</span>
                              <span className="feature-value">
                                {formatFeatureValue(value)}
                              </span>
                            </div>
                            {batchEditMode ? (
                              <input
                                type="checkbox"
                                checked={selectedFeaturesToEdit.includes(key)}
                                disabled={exhibitionModeEnabled}
                                onChange={() => { }}
                              />
                            ) : (
                              <div className="feature-button-container">
                                <button
                                  className="feature-clear-button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleFeatureClear(key);
                                  }}
                                  disabled={exhibitionModeEnabled}
                                >
                                  <img src={clearIcon} alt="clear" className="clear-icon" />
                                </button>
                                <button
                                  className="feature-edit-button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleFeatureEdit(key);
                                  }}
                                  disabled={exhibitionModeEnabled}
                                >
                                  <img src={editIcon} alt="edit" className="edit-icon" />
                                </button>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                      {batchEditMode && selectedFeaturesToEdit.length > 0 && (
                        <button
                          className="batch-edit-floating"
                          onClick={async () => {
                            const features = await Promise.all(
                              selectedFeaturesToEdit.map(name => getFeature(name))
                            );
                            setEditingFeature(features);
                            setBatchEditMode(false);
                            setSelectedFeaturesToEdit([]);
                          }}
                          disabled={exhibitionModeEnabled}
                        >
                          <span>{selectedFeaturesToEdit.length}</span>
                          →
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'info' && (
                <div className="tab-content">
                  <div className="info-section">
                  <div className="version-text" onClick={handleVersionClick}>
                    <label>{t("version")}</label>
                    <span className="version-number">
                      {`${import.meta.env.PACKAGE_VERSION}+${backendVersion || 'unknown'}`}
                    </span>
                  </div>
                    <div className="website-link">
                      <label>{t("visit")}</label>
                      <a href="https://spogen.ai" target="_blank" rel="noopener noreferrer">
                        spogen.ai
                      </a>
                    </div>

                    {showReset && (
                      <div className="exhibition-mode-section">
                        <label className="custom-checkbox">
                          <input
                            type="checkbox"
                            id="exhibitionMode"
                            checked={exhibitionModeEnabled}
                            onChange={handleIsExhibitionModeEnabledChange}
                          />
                          <span className="checkmark" />
                          <span className="radio-label">
                            {t("exhibitionMode")}
                          </span>
                        </label>
                      </div>
                    )}

                    {showReset && (
                      <div className="reset-others-section">
                        <label>{t("resetOtherDevices")}</label>
                        <button
                          className="reset-button"
                          onClick={handleResetOthersToStartScreen}
                        >
                          {t("resetNow")}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

        </div>
      )}
      {editingFeature && (
        <FeatureSelection
          features={Array.isArray(editingFeature) ? editingFeature : [editingFeature!]}
          onClose={() => setEditingFeature(null)}
          onFeatureSelect={handleFeatureSelectionComplete}
        />
      )}
      <Notification
        message={notificationMessage}
        visible={showNotification}
        onClose={() => setShowNotification(false)}
        duration={2000}
        type={notificationMessage === t('featureUpdated') || notificationMessage === t('featureCleared') ? 'success' : 'error'}
      />
    </>
  );
};

export default Settings;